import { NewButtonVariant, Title } from "@bale/ui";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import React, { useState, useCallback, useMemo } from "react";
import { strings } from "intl/fa";
import { useRequest } from "hooks/useRequest";
import { submitAdOrderThunk } from "core/orderAd/orderAd.thunk";
import { QuestionFr } from "@bale/icons";
import { TagsDropdown } from "../publishStep/TagsDropdown";
import { ApiCategory, ApiTag } from "core/configs/configs.entity";
import { useConfigs } from "hooks/useConfigs";
import commonStyles from "../Common.module.scss";
import { AdPanelDropdown } from "components/commons/dropdown/AdPanelDropdown";
import Modal from "components/commons/modal/Modal";
import { useAppDispatch, useAppSelector } from "utils/store";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import { toast } from "react-toastify";
import { targetType } from "core/orderAd/orderAd.entity";
import PriceFooter from "components/content/priceFooter/PriceFooter";
import { digitsToHindi, formatStrings } from "@bale/utils";
import { GeneralConfig } from "utils/constants";

const settingStrings = strings.dialog_ad_form.more_settings_step;

type Props = {
  isCampaign?: boolean;
};

type genderOption = { id: number; Title: string };
const genderOptions: genderOption[] = [
  // NONE option
  {
    id: 0,
    Title: settingStrings.gender_target.genders.none,
  },
  {
    id: 1,
    Title: settingStrings.gender_target.genders.male,
  },
  {
    id: 2,
    Title: settingStrings.gender_target.genders.female,
  },
];

const SettingsStep: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { categories } = useConfigs();
  const dispatch = useAppDispatch();

  const orderAdInfo = useAppSelector((state) => state.orderAd.info);
  const [showHelpModal, setShowHelpModal] = useState<boolean>(false);
  const { loading } = useRequest(submitAdOrderThunk);

  const [selectedTag, setSelectedTag] = useState<ApiTag | undefined>(undefined);
  const [subcategories, setSubcategories] = useState<ApiCategory[] | null>(
    null
  );
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<ApiCategory | null>(null);
  const [selectedGender, setSelectedGender] = useState<genderOption>(
    orderAdInfo.target_type === targetType.GENDER
      ? genderOptions[orderAdInfo.target_ids?.[0] ?? 0]
      : genderOptions[0]
  );

  const onCategoryChange = useCallback((tag: ApiTag) => {
    setSelectedTag(tag);
    setSubcategories(tag.Subcategory);
    setSelectedSubcategory(null);
  }, []);

  const onSubcategoryChange = useCallback((subcategory: ApiCategory) => {
    setSelectedSubcategory(subcategory);
  }, []);

  const onGenderChange = useCallback((gender: genderOption) => {
    if (!gender) return;
    if (gender.id === 0) {
      dispatch(
        OrderAdActions.setOrderAdInfo({
          target_ids: undefined,
          target_type: undefined,
        })
      );
      setSelectedGender(genderOptions[gender.id]);
    } else {
      dispatch(
        OrderAdActions.setOrderAdInfo({
          target_ids: [gender.id],
          target_type: targetType.GENDER,
        })
      );
      setSelectedGender(genderOptions[gender.id]);
      toast.info(
        formatStrings(
          strings.dialog_ad_form.more_settings_step.gender_target.toast,
          {
            percentage: digitsToHindi(String(GeneralConfig.GenderPrice)),
          }
        )
      );
    }
  }, []);

  const isFormInvalid = useMemo(() => {
    if (!selectedTag) {
      return true;
    }
    if (selectedTag.Subcategory && !selectedSubcategory) {
      return true;
    }

    return false;
  }, [selectedTag, selectedSubcategory]);

  const createTargetObjectData = () => {
    if (orderAdInfo.target_type === targetType.GENDER) {
      return {
        target_ids: orderAdInfo.target_ids,
        target_type: targetType.GENDER,
      };
    }
    return undefined;
  };

  const saveData = () => {
    if (isFormInvalid) {
      toast.info(strings.common.error.all_required, {
        toastId: "all_required",
      });
      return false;
    }

    dispatch(
      OrderAdActions.setOrderAdInfo({
        tag_1: selectedTag?.Id,
        tag_2: selectedSubcategory?.Id,
        ...createTargetObjectData(),
      })
    );
    return true;
  };

  const handleSubmit = () => {
    const nextPage = props.isCampaign
      ? OrderAdPages.CampaignPreview
      : OrderAdPages.DialogPreview;
    saveData() && dispatch(OrderAdActions.setPage(nextPage));
  };

  return (
    <>
      <CardWrapper className={commonStyles.Card}>
        <Title
          title={settingStrings.title}
          className={commonStyles.titleWrapper}
          suffixIcon={QuestionFr}
          suffixHandler={() => setShowHelpModal(true)}
        />

        <TagsDropdown
          categories={categories}
          subcategories={subcategories}
          onCategoryChange={onCategoryChange}
          onSubcategoryChange={onSubcategoryChange}
        />

        {!props.isCampaign && (
          <AdPanelDropdown<{ id: number; Title: string }>
            items={genderOptions}
            label={settingStrings.gender_target.title}
            onSelectedItemChange={onGenderChange}
            itemToString={(i) => i?.Title ?? ""}
            placeholderMessage={settingStrings.gender_target.example}
            selectedItemFormatter={(gender: genderOption) => gender.Title}
            helpText={formatStrings(settingStrings.gender_target.description, {
              ["percentage"]: digitsToHindi(String(GeneralConfig.GenderPrice)),
            })}
            defaultSelectedItem={selectedGender}
            scrollToWhenClicked
            openOnTop
          />
        )}
      </CardWrapper>

      <PriceFooter
        button={{
          text: strings.common.button.submit_and_continue,
          disabled: isFormInvalid,
          onClick: handleSubmit,
          loading: loading,
        }}
        isCampaign={props.isCampaign}
      />

      {showHelpModal && (
        <Modal
          title={settingStrings.title}
          primaryButton={{
            label: strings.common.button.understood,
            onClick: () => setShowHelpModal(false),
            variant: NewButtonVariant.OUTLINED,
          }}
          onClose={() => setShowHelpModal(false)}
        >
          <p className={commonStyles.modalText}>
            {settingStrings.help_modal_text}
          </p>
        </Modal>
      )}
    </>
  );
};

export default SettingsStep;
